import React, { FC, useCallback, useState } from "react";
import { Flex } from "@chakra-ui/react";
import clsx from "clsx";
import Tooltip from "rc-tooltip";
import { useTranslation } from "react-i18next";
import url from "url";
import SafeImage from "@/js/components/SafeImage";
import { CloseButton } from "@/js/components/shared/CloseButton";
import { WebsitesAndAppsListModal } from "@/components/WebsitesAndAppsListModal";
import useStore from "@/stores";
import {
	BackgroundTab,
	ClassroomStudentActivityState,
	isCompanionMode,
	platformIcon,
	trimString,
	WindowData,
} from "../../../RegularClassroomUser/utils";
import { RestrictedAppsModal } from "../../../RegularClassroomUser/components/RestrictedAppsModal";
import { User } from "@/js/utils/userUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { CompanionIcon } from "@/components/ui/custom-icons/CompanionIcon";
import ScreenshotContainer from "@/js/components/StudentTile/components/activeScreenStates/ScreenshotContainer";
import { ContextBasedStudentTile } from "js/components/StudentTile/components/activeScreenStates/ContextBasedStudentTile";
import BlankFavicon from "@/assets/studentTile/favicons/blank-favicon.svg?url";
import { getImprovedTitle } from "js/utils/screens/contextBasedScreenUtils";

interface StudentTileScreenshotProps {
	user: User;
	windowData: WindowData;
	isScreenshotLoading: boolean;
	isMonitorMode: boolean;
	isStudentClaimed: boolean;
	forceCloseTab: (tabId: string, force: boolean) => void;
	classIsActive: boolean;
	canCloseTab: boolean;
	openP2pModal?: (
		user: User,
		tabId: string | undefined,
		chromeId: string | undefined,
		windowId: string | undefined,
		isActiveTab: boolean
	) => void;
	tabItemLimit: number;
	/**
	 * Is the teacher currently restricted from seeing the screen based on the domain or app in view? (via Screen Visibility Restrictions)
	 */
	isRestricted: boolean;
	/**
	 * Is the student currently viewing a screenshare from the teacher?
	 */
	inScreenshare: boolean;
}

const StudentTileScreenshot: FC<StudentTileScreenshotProps> = (props: StudentTileScreenshotProps) => {
	const { t } = useTranslation();
	const [listModalOpen, setListModalOpen] = useState(false);

	const openP2pModal = useCallback(
		(tabId: string | undefined, isActiveTab: boolean = false) => {
			if (props.isRestricted) {
				return;
			}

			if (props.openP2pModal) {
				props.openP2pModal(props.user, tabId, props.windowData.chrome_id, props.windowData.chrome_window_id, isActiveTab);
				return;
			}
		},
		[props]
	);

	const openListModal = () => {
		setListModalOpen(true);
	};

	const closeListModal = () => {
		setListModalOpen(false);
	};

	const renderTabs = () => {
		let tabs: JSX.Element[] = [];
		if (props.windowData.background_tabs) {
			tabs = props.windowData.background_tabs
				.filter((tab) => !!tab)
				.map((tab: BackgroundTab, i: number) => {
					const isBrowserInternal = tab.tabUrl && (tab.tabUrl.startsWith("chrome") || tab.tabUrl.startsWith("edge"));

					return (
						<SafeImage
							key={tab.tabUrl + i}
							styleClassName={clsx(props.isRestricted ? "tab-favicon--noInteract" : "tab-favicon clickable")}
							title={tab.title}
							src={!tab.favIcon || isBrowserInternal ? BlankFavicon : tab.favIcon}
							alt={""}
							onClick={() => openP2pModal(tab.tab_id, false)}
						/>
					);
				});
		}
		if (tabs.length > props.tabItemLimit) {
			const ret = tabs.slice(0, props.tabItemLimit);
			const additionalTabs = Math.min(tabs.length - props.tabItemLimit, 99);
			const c = clsx("tab-favicon tab-favicon--hidden-tabs clickable", additionalTabs > 9 && "tab-favicon--small-text");
			ret.push(
				<SafeImage
					key="hidden-tabs-icon"
					styleClassName={c}
					title={t("classroomDetails.hiddenItems", { count: tabs.length - props.tabItemLimit })}
					src={""}
					alt={"+" + String(additionalTabs)}
					onClick={openListModal}
				/>
			);
			return ret;
		} else {
			return tabs;
		}
	};

	const disableCloseTab = () => {
		return props.isScreenshotLoading || !props.windowData.tab_id || !props.classIsActive || !props.isStudentClaimed;
	};

	const screenAvailable =
		!!props.windowData.screenshot &&
		(!props.windowData.state ||
			props.windowData.state === ClassroomStudentActivityState.UNSET ||
			props.windowData.state === ClassroomStudentActivityState.CACHED_SCREEN_AVAILABLE ||
			props.windowData.state === ClassroomStudentActivityState.SCREEN_AVAILABLE);

	const renderScreen = () => {
		if (props.isScreenshotLoading) {
			return <div className="RegularClassroomUser_screenshotLoading">{t("classroomDetails.loadingNewScreenshot")}</div>;
		} else {
			if (!screenAvailable) {
				return (
					<ContextBasedStudentTile
						url={props.windowData.url}
						state={props.windowData.state}
						title={props.windowData.title}
						onClick={() => openP2pModal(props.windowData.tab_id, true)}
						clickable={!props.isRestricted}
					/>
				);
			}

			return props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
				<ScreenshotContainer onClick={() => openP2pModal(props.windowData.tab_id, true)} clickable={!props.isRestricted}>
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(props.windowData.title, 40)}
						src={props.windowData.screenshot}
					/>
				</ScreenshotContainer>
			) : (
				<ScreenshotContainer clickable={false}>
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(props.windowData.title, 40)}
						src={props.windowData.screenshot}
					/>
				</ScreenshotContainer>
			);
		}
	};

	return (
		<>
			<div className="RegularClassroomUser_screenHeader">
				{props.inScreenshare && (
					<Flex mr={4} width={"24px"} flexShrink={0}>
						<ChakraFAIcon icon="fa-screencast" variant="solid" boxSize={5} color={"#0075DB"} />
					</Flex>
				)}
				{props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
					props.isRestricted ? (
						<RestrictedAppsModal
							screenshotData={props.windowData}
							isMonitorMode={props.isMonitorMode}
							isStudentClaimed={props.isStudentClaimed}
							isClassActive={props.classIsActive}
							forceCloseTab={props.forceCloseTab}
							canCloseTab={props.canCloseTab}
							student={props.user}
						/>
					) : (
						<Tooltip
							placement="bottomLeft"
							trigger={url.parse(props.windowData.url).protocol ? ["hover"] : []}
							destroyTooltipOnHide={true}
							overlayClassName={"Tooltip_OpenNewTabHyperlink"}
							overlay={
								<a href={props.windowData.url} target="_blank" rel="noopener noreferrer">
									{t("classroomDetails.openInNewTab")}
								</a>
							}
						>
							<span
								className={clsx("RegularClassroomUser_screenHeaderText", !props.isRestricted && "clickable")}
								title={props.windowData.title}
								data-testid="button-live-view-modal"
								onClick={() => openP2pModal(props.windowData.tab_id, true)}
							>
								{!props.isScreenshotLoading && getImprovedTitle(props.windowData.title)}
							</span>
						</Tooltip>
					)
				) : (
					<span className={"RegularClassroomUser_screenHeaderText"} title={props.windowData.title}>
						{!props.isScreenshotLoading && props.windowData.title}
					</span>
				)}
				{props.windowData.url !== "" && !props.isRestricted && (
					<Flex marginLeft={"auto"}>
						{props.canCloseTab && (
							<CloseButton
								tooltipText={t("classroomDetails.closeTab")}
								tooltipId={props.windowData.tab_id + "_closeTabTooltip"}
								disabled={disableCloseTab()}
								onClick={() => {
									props.forceCloseTab(props.windowData.tab_id, true);
								}}
							/>
						)}
					</Flex>
				)}
			</div>
			{renderScreen()}
			<WebsitesAndAppsListModal
				title={t("classroomDetails.openTabs")}
				open={listModalOpen}
				activeTab={{
					...props.windowData,
					id: props.windowData.tab_id,
					active: true,
				}}
				items={props.windowData.background_tabs || []}
				restrictedItems={[]}
				studentUsername={props.user.username}
				onClose={closeListModal}
				forceCloseTab={props.forceCloseTab}
				canCloseTab={props.canCloseTab}
				classIsActive={props.classIsActive}
				isMonitorMode={props.isMonitorMode}
				isStudentClaimed={props.isStudentClaimed}
			/>
			<div className="RegularClassroomUser_tabRows">
				{props.windowData.background_tabs && props.windowData.background_tabs.length > 0 && (
					<div className="RegularClassroomUser_tabRow">
						<button data-testid="button-open-info-modal" className="RegularClassroomUser_tabsAgentIcon" onClick={openListModal}>
							{isCompanionMode(props.windowData) ? (
								<CompanionIcon color="inherit" boxSize={4} />
							) : (
								<ChakraFAIcon
									icon={platformIcon(props.windowData).iconName}
									variant={platformIcon(props.windowData).variant}
									color="inherit"
									boxSize={4}
								/>
							)}
						</button>
						{renderTabs()}
					</div>
				)}
			</div>
		</>
	);
};

export default StudentTileScreenshot;
